import { useCallback, useMemo } from 'react';
import { useSeqConsultContext } from '@/providers/SeqConsultProvider';
import { useFormContext, useWatch } from 'react-hook-form';
import { SeqForm } from '@/types/seq-consult';
import { CONSULT_TYPE_DATA } from '@/constants/seq-consult';
import useAuthenticated from '@/hooks/useAuthenticated';

const useSeqConsultControls = () => {
  const { watch, control } = useFormContext<SeqForm>();
  const { isAuthenticated } = useAuthenticated();

  const {
    step,
    handleNext,
    handlePrev,
    handleSubmit,
    isTelCertificated,
    consultType,
  } = useSeqConsultContext();

  const watchName = useWatch({
    name: 'name',
    control,
  });

  const watchTel = useWatch({
    name: 'tel',
    control,
  });

  const watchAgreementPrivacy = useWatch({
    name: 'agreement.privacy',
    control,
  });

  const watchAgreementProvide = useWatch({
    name: 'agreement.provide',
    control,
  });

  const isFirstStep = useMemo(() => {
    if (consultType === 'MATCH') {
      return step === 'baseConstructionPlan.age';
    }
    return step === 'site.buildingType';
  }, [step, consultType]);

  const disabledNextStep = useCallback(() => {
    if (step === 'baseConstructionPlan.scopes') {
      const scopeValues = watch(step);

      const hasEtc = scopeValues?.includes('ETC');
      if (hasEtc) {
        const etcCommentLength = watch(
          'baseConstructionPlan.etcComment',
        )?.length;

        return (
          !etcCommentLength || etcCommentLength > 20 || !scopeValues?.length
        );
      } else {
        return !scopeValues?.length;
      }
    }

    if (step === 'personal') {
      const site = watch('site');
      const base =
        watchName &&
        watchTel &&
        watchAgreementPrivacy &&
        watchAgreementProvide &&
        site?.address &&
        site?.addressDetail;

      const isPassedPersonalStep = isAuthenticated
        ? base
        : base && isTelCertificated;

      return !isPassedPersonalStep;
    }

    // 값이 0인 경우에도 다음 버튼이 활성화 될 수 있도록 예외처리
    if (watch(step) === 0) {
      return false;
    }

    return !watch(step);
  }, [
    isAuthenticated,
    isTelCertificated,
    step,
    watch,
    watchAgreementPrivacy,
    watchAgreementProvide,
    watchName,
    watchTel,
  ]);

  const isLastStep = useMemo(() => step === 'personal', [step]);

  const getGtmKey = useCallback(
    (step: string) => {
      const currentConsultData = CONSULT_TYPE_DATA[consultType];
      return currentConsultData[step as keyof typeof currentConsultData]
        ?.gtmKey;
    },
    [consultType],
  );

  const prevButtonGtmKey = useMemo(() => {
    const gtmKey = getGtmKey(step);
    return gtmKey ? `seq_${gtmKey}_prev_consult` : '';
  }, [step, getGtmKey]);

  const nextButtonGtmKey = useMemo(() => {
    const gtmKey = getGtmKey(step);
    return gtmKey ? `seq_${gtmKey}_next_consult` : '';
  }, [step, getGtmKey]);

  return {
    isFirstStep,
    handlePrev,
    handleNext,
    disabledNextStep,
    isLastStep,
    handleSubmit,
    prevButtonGtmKey,
    nextButtonGtmKey,
  };
};

export default useSeqConsultControls;
